import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { adminColors, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminFormFieldSx,
} from '../../styles/adminComponentStyles';

type ReceiptNotesDialogMode = 'notes' | 'reject';

type ReceiptNotesDialogProps = {
  open: boolean;
  title?: string;
  mode?: ReceiptNotesDialogMode;
  reviewNotes?: string | null;
  loading?: boolean;
  saving?: boolean;
  onClose: () => void;
  onAddNote?: (note: string) => Promise<void> | void;
  onReject?: (note: string) => Promise<void> | void;
};

const NOTE_SEPARATOR_PATTERN = /\n\s*---\s*\n/g;

const ReceiptNotesDialog: React.FC<ReceiptNotesDialogProps> = ({
  open,
  title = 'Receipt Notes',
  mode = 'notes',
  reviewNotes,
  loading = false,
  saving = false,
  onClose,
  onAddNote,
  onReject,
}) => {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const entries = useMemo(
    () => (reviewNotes || '')
      .split(NOTE_SEPARATOR_PATTERN)
      .map((entry) => entry.trim())
      .filter(Boolean),
    [reviewNotes],
  );
  const trimmedNote = note.trim();
  const noteRequired = mode === 'reject';
  const showError = touched && noteRequired && !trimmedNote;

  const handleClose = () => {
    if (saving) return;
    setNote('');
    setTouched(false);
    onClose();
  };

  const handleAddNote = async () => {
    setTouched(true);
    if (!trimmedNote || !onAddNote) return;
    await onAddNote(trimmedNote);
    setNote('');
    setTouched(false);
  };

  const handleReject = async () => {
    setTouched(true);
    if (!trimmedNote || !onReject) return;
    await onReject(trimmedNote);
    setNote('');
    setTouched(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }) }}
    >
      <DialogTitle
        sx={adminCombineSx(adminDialogTitleSx, {
          backgroundColor: 'rgb(239, 246, 255)',
          color: adminColors.primary,
          fontWeight: 600,
          marginBottom: 0,
        })}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
        <Box
          sx={{
            minHeight: 180,
            maxHeight: 360,
            overflowY: 'auto',
            bgcolor: '#FFFDF2',
            border: `1px solid ${adminColors.borderHover}`,
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          {loading ? (
          <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText }}>Loading notes...</Typography>
          ) : entries.length > 0 ? (
            entries.map((entry, index) => (
              <Box
                key={`${index}-${entry.slice(0, 20)}`}
                sx={{
                  borderBottom: index === entries.length - 1 ? 0 : '1px dashed #B8C0CC',
                  pb: index === entries.length - 1 ? 0 : 1.5,
                  mb: index === entries.length - 1 ? 0 : 1.5,
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: '"Courier New", Courier, monospace',
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: '#1F2937',
                  }}
                >
                  {entry}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography
              component="pre"
              sx={{
                m: 0,
                fontFamily: '"Courier New", Courier, monospace',
                fontSize: 13,
                color: '#6B7280',
              }}
            >
              No notes yet.
            </Typography>
          )}
        </Box>

        <TextField
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => setTouched(true)}
          label={mode === 'reject' ? 'Rejection note' : 'Add note'}
          placeholder={mode === 'reject' ? 'Enter the reason for rejection' : 'Type a note'}
          multiline
          minRows={4}
          fullWidth
          error={showError}
          helperText={showError ? 'A note is required to reject this receipt.' : ' '}
          disabled={saving}
          sx={adminFormFieldSx}
        />
      </DialogContent>
      <DialogActions sx={adminDialogActionsSx}>
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          size="small"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
        >
          Cancel
        </Button>
        {mode === 'reject' ? (
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            size="small"
            disabled={saving || !trimmedNote}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.danger, { height: 40 })}
          >
            {saving ? 'Rejecting...' : 'Reject'}
          </Button>
        ) : (
          <Button
            onClick={handleAddNote}
            variant="contained"
            size="small"
            disabled={saving || !trimmedNote}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
          >
            {saving ? 'Saving...' : 'Add Note'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptNotesDialog;
